/**
 * Regras puras da reconciliação da lista de aplicativos do portal do usuário final (R2CLOUD-1251),
 * fora do componente e do serviço Angular para poderem ser testadas sem TestBed — mesmo arranjo de
 * `http-unauthorized-rules.ts` e `contingency-sync-rules.ts`.
 *
 * O defeito: o tópico STOMP `${USER_TOPIC}/remote-apps-ready` entrega a lista COMPLETA a cada
 * emissão, mas os dois consumidores só sabiam somar (atualiza-se-existe / adiciona-se-não-existe).
 * Quando o administrador revogava o acesso de um usuário a um aplicativo, o app sumia da lista
 * enviada pelo servidor e mesmo assim continuava na lista em memória do browser — aparecendo e
 * abrindo depois de F5 e de "limpar o cache do navegador".
 *
 * Como a lista recebida é completa, o que não está nela não existe mais para aquele usuário: dá
 * para reconciliar com segurança, sem precisar de um evento de remoção vindo do servidor.
 */

/** O mínimo que a regra precisa saber de um item da lista recebida do servidor (`RemoteAppReady`). */
export interface RemoteAppReadyLike {
    remoteAppId: number;
    serviceApp?: boolean | null;
    hasGuacServer?: boolean | null;
}

/** O mínimo que a regra precisa saber de um item da lista local (wrapper do desktop ou `RemoteAppHtml5`). */
export interface RemoteAppIdentified {
    remoteAppId: number | null;
}

/** O que fica (na ordem original, com os mesmos objetos) e o que sai da lista local. */
export interface RemoteAppReconciliation<T extends RemoteAppIdentified> {
    kept: T[];
    removed: T[];
}

/** Filtro de elegibilidade do portal HTML5: app de serviço só entra se o servidor tiver Guacamole. */
export function isRemoteAppHtml5Eligible(remoteAppReady: RemoteAppReadyLike): boolean {
    return !remoteAppReady.serviceApp || !!remoteAppReady.hasGuacServer;
}

/**
 * Ids que a lista recebida autoriza. `isEligible` permite que um consumidor considere ausente
 * também o app que continua na lista mas deixou de ser elegível para ele — é o caso do portal
 * HTML5, onde um app de serviço que perdeu o servidor Guacamole precisa sair da tela.
 */
export function collectAllowedRemoteAppIds(
    remoteAppReadyList: readonly RemoteAppReadyLike[],
    isEligible?: (remoteAppReady: RemoteAppReadyLike) => boolean
): Set<number> {
    const allowedRemoteAppIds = new Set<number>();
    for (const remoteAppReady of remoteAppReadyList) {
        if (!isEligible || isEligible(remoteAppReady)) {
            allowedRemoteAppIds.add(remoteAppReady.remoteAppId);
        }
    }
    return allowedRemoteAppIds;
}

/**
 * Separa a lista local em "fica" e "sai" a partir da lista recebida. Preserva a referência dos
 * objetos mantidos — o status já acumulado neles (e o `loading` em andamento) não pode ser perdido
 * só porque outro app foi revogado.
 *
 * Item local sem `remoteAppId` não tem como ser confirmado pela lista recebida e sai: ele nunca
 * casaria com nada e ficaria preso na tela para sempre.
 *
 * Lista recebida vazia esvazia a lista local — é resposta legítima do servidor (usuário sem nenhum
 * app), não erro. Quem NÃO pode chamar esta função é o ramo de erro do STOMP ou a falha de leitura
 * do IndexedDB: ali não houve lista nenhuma, e esvaziar a tela por causa de uma queda de conexão
 * seria um estrago maior que o defeito. Use `isAuthoritativeRemoteAppList` para separar os casos.
 */
export function reconcileRemoteAppList<T extends RemoteAppIdentified>(
    localList: readonly T[],
    remoteAppReadyList: readonly RemoteAppReadyLike[],
    isEligible?: (remoteAppReady: RemoteAppReadyLike) => boolean
): RemoteAppReconciliation<T> {
    const allowedRemoteAppIds = collectAllowedRemoteAppIds(remoteAppReadyList, isEligible);
    const kept: T[] = [];
    const removed: T[] = [];
    for (const item of localList) {
        if (item.remoteAppId != null && allowedRemoteAppIds.has(item.remoteAppId)) {
            kept.push(item);
        } else {
            removed.push(item);
        }
    }
    return {kept, removed};
}

/**
 * Existe lista para reconciliar? Lista vazia é resposta; ausência de lista (erro de STOMP, snapshot
 * inexistente, leitura do IndexedDB que falhou) não é. A distinção é o que impede a tela do usuário
 * de esvaziar por causa de uma queda de conexão.
 */
export function isAuthoritativeRemoteAppList(
    remoteAppReadyList: readonly RemoteAppReadyLike[] | null | undefined
): remoteAppReadyList is readonly RemoteAppReadyLike[] {
    return Array.isArray(remoteAppReadyList);
}
